import type Phaser from 'phaser';
import type { CountInPlan } from 'Models/CountIn';
import {
  countdownFor,
  countdownFrameAt,
  countedClicksCountdown,
  countedClicksNowBars,
  type Countdown,
  type CountedClicks,
} from 'Models/CountInCountdown';
import type ITimeKeeper from 'Models/ITimeKeeper';
import { isPlayableTimeSignature } from 'Models/MeterTiming';
import { colors } from 'Phaser/config';
import { transportGridBarsNow } from 'Utils/playAlongCountIn';

/** The cursor the countdown sits on: its world-space box. */
export interface CountdownAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A point in world space. */
export interface WorldPoint {
  x: number;
  y: number;
}

/** A box in world space the number keeps clear of (score text). */
export interface WorldBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Where the number goes: above the cursor (sight reading), or above it and to
 * its right, clear of the tempo mark and the fingering a score prints over
 * its first note (repertoire).
 */
export type CountdownNumberPlacement = 'aboveCursor' | 'aboveRightOfCursor';

/** What the scene hands the countdown each frame. */
export interface CountdownInput {
  /** The scheduler's count-in for the current start, or null (graded, or none). */
  countIn: { plan: CountInPlan } | null;
  timeKeeper: ITimeKeeper;
  pickupMeasures: number;
  /** The cursor waiting on the first note; null hides the countdown. */
  anchor: CountdownAnchor | null;
  /**
   * The centre of the entry's highest notehead in world space, asked once per
   * count-in plan; null (or no lookup) rings the cursor's middle instead.
   */
  entryHeadOf?: (plan: CountInPlan) => WorldPoint | null;
  /**
   * Score text near the cursor (a tempo mark, fingerings), asked once per
   * count-in plan: the number moves up above any of it in its way.
   */
  textObstaclesOf?: (plan: CountInPlan) => WorldBox[];
  /**
   * How much of the top of the canvas page chrome covers (a toolbar laid
   * over it), in canvas pixels, read once per count-in plan: the number is
   * kept below it.
   */
  coveredCanvasTopOf?: (plan: CountInPlan) => number;
}

/**
 * What a scene that counts its own count-in clicks (the tutorial exercises)
 * hands the countdown each frame.
 */
export interface CountedCountdownInput {
  /** The count-in now, or null (graded, paused, or no count-in running). */
  countIn: CountedClicks | null;
  /** The cursor waiting on the first note; null hides the countdown. */
  anchor: CountdownAnchor | null;
  /**
   * The first note in world space, with the ring's radius when the note is
   * drawn larger than a staff's notehead; null rings the cursor's middle.
   */
  entryHead?: () => RingTarget | null;
}

/** Where the ring goes: a point, and its radius when the scene sizes it. */
export interface RingTarget extends WorldPoint {
  radius?: number;
}

/** Where the ring goes and what the number keeps clear of. */
interface RingLayout {
  head: RingTarget | null;
  obstacles: WorldBox[];
  coveredCanvasTop: number;
}

/** What is looked up once per count-in plan and cursor box. */
interface PlanLayout extends RingLayout {
  plan: CountInPlan;
  /** The cursor box the lookup was made for: a resize or rotation moves it. */
  anchorKey: string;
}

/**
 * The bottom of a number of `heightPx` spanning `left`..`right`, moved up
 * from `bottom` above every box in its way (boxes stacked above each other
 * are cleared one after another).
 */
export const numberBottomClearOf = (
  obstacles: readonly WorldBox[],
  {
    left,
    right,
    bottom,
    heightPx,
    gapPx,
  }: {
    left: number;
    right: number;
    bottom: number;
    heightPx: number;
    gapPx: number;
  },
): number =>
  [...obstacles]
    .filter((box) => box.right > left && box.left < right)
    .sort((lower, upper) => upper.bottom - lower.bottom)
    .reduce(
      (current, box) =>
        box.bottom > current - heightPx && box.top < current
          ? Math.min(current, box.top - gapPx)
          : current,
      bottom,
    );

/**
 * Where a number of `widthPx` x `heightPx` goes: at `left` / `bottom` when
 * that is clear of every box; else slid right past the boxes in its way, at
 * the same height, while that keeps it within `maxShiftPx` of `left`; else
 * moved up above them (numberBottomClearOf).
 */
export const numberSpotClearOf = (
  obstacles: readonly WorldBox[],
  {
    left,
    bottom,
    widthPx,
    heightPx,
    gapPx,
    maxShiftPx,
  }: {
    left: number;
    bottom: number;
    widthPx: number;
    heightPx: number;
    gapPx: number;
    maxShiftPx: number;
  },
): { left: number; bottom: number } => {
  const inTheWay = (at: number): WorldBox[] =>
    obstacles.filter(
      (box) =>
        box.right > at &&
        box.left < at + widthPx &&
        box.bottom > bottom - heightPx &&
        box.top < bottom,
    );
  // Each step passes at least one more box, so as many steps as boxes clears them all.
  const slid = obstacles.reduce((at) => {
    const blocking = inTheWay(at);
    return blocking.length === 0 ? at : Math.max(...blocking.map((box) => box.right)) + gapPx;
  }, left);
  if (slid - left <= maxShiftPx) {
    return { left: slid, bottom };
  }
  return {
    left,
    bottom: numberBottomClearOf(obstacles, {
      left,
      right: left + widthPx,
      bottom,
      heightPx,
      gapPx,
    }),
  };
};

/** The entry of a count-in plan as a score timestamp, in whole notes. */
export const entryWholeNotes = (
  plan: CountInPlan,
  pickupMeasures: number,
  wholeNotesPerMeasure: number,
): number => (plan.entryBars - pickupMeasures) * wholeNotesPerMeasure;

/** The highest-pitched of a set of notes (the top notehead of a chord), or null. */
export const highestNote = <Note extends { MidiNote: number }>(
  notes: readonly Note[],
): Note | null =>
  notes.reduce<Note | null>(
    (highest, note) => (highest === null || note.MidiNote > highest.MidiNote ? note : highest),
    null,
  );

/** Something drawn with world-space bounds: a notehead image, a beat number. */
export interface BoundedGraphic {
  getBounds(): { centerX: number; centerY: number; width?: number; height?: number };
}

/** Space between a graphic and the ring drawn round it. */
const RING_GAP_PX = 8;

/** A ring round the whole of `graphic` (a large notehead, a beat number). */
export const ringAround = (graphic: BoundedGraphic): RingTarget => {
  const { centerX, centerY, width = 0, height = 0 } = graphic.getBounds();
  return { x: centerX, y: centerY, radius: Math.max(width, height) / 2 + RING_GAP_PX };
};

/** Heads closer than this (px) across count as one chord. */
const SAME_COLUMN_PX = 1;

/**
 * The centre of the first note's head among `heads`: the leftmost, and of a
 * chord there the highest on screen. Null without heads.
 */
export const firstHeadCenter = (heads: readonly BoundedGraphic[]): WorldPoint | null => {
  const centers = heads.map((head) => head.getBounds());
  if (centers.length === 0) return null;
  const left = Math.min(...centers.map((center) => center.centerX));
  const top = centers
    .filter((center) => center.centerX - left < SAME_COLUMN_PX)
    .reduce((highest, center) => (center.centerY < highest.centerY ? center : highest));
  return { x: top.centerX, y: top.centerY };
};

/** Above the cursors (which draw at the scene's default depths). */
const COUNTDOWN_DEPTH = 1000;
/** The number's size, as a share of the cursor's height, within these bounds. */
const NUMBER_SIZE_OF_CURSOR = 0.6;
const MIN_NUMBER_PX = 28;
const MAX_NUMBER_PX = 72;
/** Gap between the number and the top of the cursor. */
const NUMBER_GAP_PX = 6;
/** How far (in number heights) the number may slide right to clear score text. */
const MAX_NUMBER_SLIDE_OF_SIZE = 2;
/** The cursor box as a cache key, to half a pixel. */
const anchorKeyOf = (anchor: CountdownAnchor): string =>
  [anchor.x, anchor.y, anchor.width, anchor.height].map((value) => Math.round(value * 2)).join(',');
/** Gap between the number and the cursor's right edge (repertoire). */
const NUMBER_SIDE_GAP_PX = 4;
/** The ring around the entry's notehead: about the cursor's width, within bounds. */
const MIN_HEAD_RING_RADIUS_PX = 16;
const MAX_HEAD_RING_RADIUS_PX = 32;
const HEAD_RING_RADIUS_OF_CURSOR_WIDTH = 1.1;
/** Without a notehead: around the cursor's middle, half the cursor's height within bounds. */
const MIN_RING_RADIUS_PX = 18;
const MAX_RING_RADIUS_PX = 56;
const RING_RADIUS_OF_CURSOR = 0.5;
const RING_LINE_PX = 4;
const RING_TRACK_ALPHA = 0.18;
const NUMBER_COLOR = '#4a5fd0';

/**
 * The count-in's countdown number (above the cursor) and closing ring
 * (around the first note), for ungraded play. Each frame `update` reads the
 * audio clock through the transport and the count-in plan the scheduler
 * queued, so the number changes and the ring closes on the clicks' own times
 * and follows a tempo change during the count-in. Anything other than a
 * count-in in progress (paused, stopped, graded, before the first click, from
 * the entry on) draws nothing.
 */
export default class CountInCountdownView {
  private text: Phaser.GameObjects.Text | null = null;
  private ring: Phaser.GameObjects.Graphics | null = null;
  private cached: { plan: CountInPlan; measureSecs: number; countdown: Countdown } | null = null;
  private cachedLayout: PlanLayout | null = null;
  private shownValue: number | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly numberPlacement: CountdownNumberPlacement = 'aboveCursor',
  ) {}

  update({
    countIn,
    timeKeeper,
    pickupMeasures,
    anchor,
    entryHeadOf,
    textObstaclesOf,
    coveredCanvasTopOf,
  }: CountdownInput): void {
    const nowBars =
      countIn === null || anchor === null ? null : transportGridBarsNow(timeKeeper, pickupMeasures);
    const countdown =
      nowBars === null || countIn === null ? null : this.countdownOf(countIn.plan, timeKeeper);
    const frame =
      countdown === null || nowBars === null ? null : countdownFrameAt(countdown, nowBars);
    if (frame === null || anchor === null) {
      this.clear();
      return;
    }
    this.draw(
      frame.value,
      frame.ringRemaining,
      anchor,
      this.layoutFor(countIn, anchor, { entryHeadOf, textObstaclesOf, coveredCanvasTopOf }),
    );
  }

  /**
   * The countdown for a scene that counts its own count-in clicks. The first
   * note's head is read every frame (one bounds read), so nothing cached can
   * go stale between passes.
   */
  updateCounted({ countIn, anchor, entryHead }: CountedCountdownInput): void {
    const nowBars = countIn === null ? null : countedClicksNowBars(countIn);
    const frame =
      countIn === null || nowBars === null
        ? null
        : countdownFrameAt(countedClicksCountdown(countIn.beats), nowBars);
    if (frame === null || anchor === null) {
      this.clear();
      return;
    }
    this.draw(frame.value, frame.ringRemaining, anchor, {
      head: entryHead?.() ?? null,
      obstacles: [],
      coveredCanvasTop: 0,
    });
  }

  /** The number on screen now (null when nothing is drawn). */
  getShownValue(): number | null {
    return this.shownValue;
  }

  /** Hide everything (pause, stop, restart). */
  clear(): void {
    this.shownValue = null;
    this.text?.setVisible(false);
    this.ring?.clear();
  }

  /** Remove the objects (unmount). */
  destroy(): void {
    this.shownValue = null;
    this.cached = null;
    this.cachedLayout = null;
    this.text?.destroy();
    this.ring?.destroy();
    this.text = null;
    this.ring = null;
  }

  private countdownOf(plan: CountInPlan, timeKeeper: ITimeKeeper): Countdown | null {
    const timeSignature = timeKeeper.getTimeSignature();
    const measureSecs = timeKeeper.getMeasureDurationSecs();
    if (!isPlayableTimeSignature(timeSignature) || !(measureSecs > 0)) return null;
    const cached = this.cached;
    if (cached !== null && cached.plan === plan && cached.measureSecs === measureSecs) {
      return cached.countdown;
    }
    const countdown = countdownFor(plan, timeSignature, measureSecs);
    this.cached = { plan, measureSecs, countdown };
    return countdown;
  }

  /** The entry's notehead and the score text to avoid, looked up once per count-in plan. */
  private layoutFor(
    countIn: { plan: CountInPlan } | null,
    anchor: CountdownAnchor,
    {
      entryHeadOf,
      textObstaclesOf,
      coveredCanvasTopOf,
    }: Pick<CountdownInput, 'entryHeadOf' | 'textObstaclesOf' | 'coveredCanvasTopOf'>,
  ): PlanLayout | null {
    if (countIn === null) return null;
    const anchorKey = anchorKeyOf(anchor);
    if (this.cachedLayout?.plan !== countIn.plan || this.cachedLayout.anchorKey !== anchorKey) {
      this.cachedLayout = {
        plan: countIn.plan,
        anchorKey,
        head: entryHeadOf?.(countIn.plan) ?? null,
        obstacles: textObstaclesOf?.(countIn.plan) ?? [],
        coveredCanvasTop: coveredCanvasTopOf?.(countIn.plan) ?? 0,
      };
    }
    return this.cachedLayout;
  }

  private draw(
    value: number,
    ringRemaining: number,
    anchor: CountdownAnchor,
    layout: RingLayout | null,
  ): void {
    const head = layout?.head ?? null;
    const centerX = head?.x ?? anchor.x + anchor.width / 2;
    const centerY = head?.y ?? anchor.y + anchor.height / 2;
    const radius =
      head?.radius ??
      (head === null
        ? Math.min(
            MAX_RING_RADIUS_PX,
            Math.max(MIN_RING_RADIUS_PX, anchor.height * RING_RADIUS_OF_CURSOR),
          )
        : Math.min(
            MAX_HEAD_RING_RADIUS_PX,
            Math.max(MIN_HEAD_RING_RADIUS_PX, anchor.width * HEAD_RING_RADIUS_OF_CURSOR_WIDTH),
          ));
    const ring = this.ringGraphics();
    ring.clear();
    ring.lineStyle(RING_LINE_PX, colors.cursor, RING_TRACK_ALPHA);
    ring.strokeCircle(centerX, centerY, radius);
    if (ringRemaining > 0) {
      const top = -Math.PI / 2;
      ring.lineStyle(RING_LINE_PX, colors.cursor, 1);
      ring.beginPath();
      ring.arc(centerX, centerY, radius, top, top + ringRemaining * Math.PI * 2, false);
      ring.strokePath();
    }

    const sizePx = Math.round(
      Math.min(MAX_NUMBER_PX, Math.max(MIN_NUMBER_PX, anchor.height * NUMBER_SIZE_OF_CURSOR)),
    );
    const text = this.numberText();
    text.setFontSize(sizePx);
    text.setText(String(value));
    const placedRight = this.numberPlacement === 'aboveRightOfCursor';
    const numberX = placedRight
      ? anchor.x + anchor.width + NUMBER_SIDE_GAP_PX
      : anchor.x + anchor.width / 2;
    const widthPx = text.width > 0 ? text.width : sizePx * String(value).length;
    const spot = numberSpotClearOf(layout?.obstacles ?? [], {
      left: placedRight ? numberX : numberX - widthPx / 2,
      bottom: Math.min(anchor.y, centerY - radius) - NUMBER_GAP_PX,
      widthPx,
      heightPx: sizePx,
      gapPx: NUMBER_GAP_PX,
      // Beside the cursor it may slide right past a tempo mark; centred over
      // the cursor it stays centred.
      maxShiftPx: placedRight ? sizePx * MAX_NUMBER_SLIDE_OF_SIZE : 0,
    });
    const clearOfScore = spot.bottom;
    // Kept inside the view: with little room above the staff (a phone in
    // landscape) the number comes down over the cursor rather than off screen.
    // Kept below page chrome laid over the canvas top (the repertoire toolbar).
    const camera = this.scene.cameras?.main;
    const viewTop =
      camera === undefined
        ? undefined
        : camera.worldView.y + (layout?.coveredCanvasTop ?? 0) / (camera.zoom || 1);
    const numberBottom =
      viewTop === undefined
        ? clearOfScore
        : Math.max(clearOfScore, viewTop + (text.height > 0 ? text.height : sizePx));
    text.setPosition(placedRight ? spot.left : numberX, numberBottom);
    text.setVisible(true);
    this.shownValue = value;
  }

  private ringGraphics(): Phaser.GameObjects.Graphics {
    this.ring ??= this.scene.add.graphics().setDepth(COUNTDOWN_DEPTH);
    return this.ring;
  }

  private numberText(): Phaser.GameObjects.Text {
    this.text ??= this.scene.add
      .text(0, 0, '', {
        fontFamily: 'Lato, sans-serif',
        fontStyle: 'bold',
        color: NUMBER_COLOR,
      })
      .setOrigin(this.numberPlacement === 'aboveRightOfCursor' ? 0 : 0.5, 1)
      .setDepth(COUNTDOWN_DEPTH);
    return this.text;
  }
}
